import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, Check } from "lucide-react";
import PricingModal from "./MockInterview/PricingModal";
import NotificationBell from "./Notifications/NotificationBell";
import EmailVerificationBanner from "./authentication/EmailVerificationBanner";
import { hasRole } from "../utils/auth";
import { useSubscription } from "../context/SubscriptionContext";
import "./TopMenu.css";

const menuItems = [
  { label: "AI Resume Builder", path: "/resume-builder" },
  { label: "College Campus to Job" },
  { label: "Courses" },
  { label: "1-1 Mock Interview", path: "/mock-interview" },
  { label: "Codeverse" },
];

const isMobile = () => window.innerWidth <= 768;

const TopMenu = ({ theme, toggleTheme }) => {
  const [hovered, setHovered] = useState(false);
  const [signupActive, setSignupActive] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [showPricing, setShowPricing] = useState(false);

  const navigate = useNavigate();
  const { subscription, refresh: refreshPlan } = useSubscription();

  // True for plain candidates only — admins / interviewers don't pay.
  const isCandidate = isLoggedIn && hasRole("User") && !hasRole("Admin") && !hasRole("Interviewer");

  // Free users see "Upgrade"; paid users see "{displayName} Plan" in the pill.
  // We still let paid users click the pill so they can move from Basic → Pro.
  const planKey = subscription?.planKey || "Free";
  const isFreePlan = planKey === "Free";
  const planLabel = isFreePlan
    ? "Upgrade"
    : `${subscription?.displayName || planKey} Plan`;

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");

    if (token && userData) {
      setIsLoggedIn(true);
      setUser(JSON.parse(userData));
    } else {
      setIsLoggedIn(false);
    }
  }, []);

  const handleLoginClick = () => {
    if (isMobile()) {
      setSignupActive((active) => !active);
    }
    navigate("/login");
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsLoggedIn(false);
    setUser(null);
    setProfileMenuOpen(false);
    navigate("/login");
  };

  const handleDashboard = () => {
    navigate("/dashboard");
  };

  React.useEffect(() => {
    const handleResize = () => {
      if (!isMobile() && signupActive) setSignupActive(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [signupActive]);

  return (
    <>
      {isLoggedIn && <EmailVerificationBanner />}
      <header className={`top-menu theme-${theme}`}>
      <div className="logo desktop-only" onClick={() => navigate("/")}>
        LOGO
      </div>
      <div className="logo-mobile mobile-only">
        <button
          type="button"
          className="menu-burger"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open menu"
        >
          <svg width="28" height="28" fill="currentColor" viewBox="0 0 24 24">
            <rect x="4" y="7" width="16" height="2" rx="1" />
            <rect x="4" y="12" width="16" height="2" rx="1" />
            <rect x="4" y="17" width="16" height="2" rx="1" />
          </svg>
        </button>
        <span className="logo" onClick={() => navigate("/")}>
          LOGO
        </span>
      </div>

      <nav className="menu-center">
        <ul>
          {menuItems.map((item) => (
            <li key={item.label}>
              {item.path ? (
                <a
                  href={item.path}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(item.path);
                  }}
                >
                  {item.label}
                </a>
              ) : (
                <a href="#features">{item.label}</a>
              )}
            </li>
          ))}
        </ul>
      </nav>

      <div className="menu-right">
        {isCandidate && (
          <button
            type="button"
            className={`tm-upgrade-btn ${isFreePlan ? '' : 'tm-upgrade-btn-active'}`}
            onClick={() => setShowPricing(true)}
            title={isFreePlan ? "View pricing" : `Current plan: ${planLabel}`}
          >
            {isFreePlan && <Zap size={14} />}
            <span>{planLabel}</span>
            {!isFreePlan && <Check size={14} />}
          </button>
        )}
        {isLoggedIn && <NotificationBell />}
        {isLoggedIn ? (
          <div
            className="user-profile-menu"
            onMouseEnter={() => !isMobile() && setProfileMenuOpen(true)}
            onMouseLeave={() => !isMobile() && setProfileMenuOpen(false)}
          >
            <button
              className={`profile-button ${profileMenuOpen ? "active" : ""}`}
              onClick={() =>
                isMobile()
                  ? setProfileMenuOpen(!profileMenuOpen)
                  : handleDashboard()
              }
            >
              <div className="profile-avatar">
                {user?.fullName?.charAt(0).toUpperCase()}
              </div>
            </button>

            <div className={`profile-dropdown ${profileMenuOpen ? "open" : ""}`}>
              <div className="dd-user-header">
                <div className="dd-user-avatar">
                  {user?.fullName?.charAt(0).toUpperCase()}
                </div>
                <div className="dd-user-info">
                  <div className="dd-user-name">{user?.fullName}</div>
                  <div className="dd-user-email">{user?.email}</div>
                </div>
              </div>

              <div className="dd-divider" />

              <button className="dd-item" onClick={() => { setProfileMenuOpen(false); navigate("/profile"); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 0 0-16 0"/></svg>
                My Profile
              </button>
              <button className="dd-item" onClick={() => { setProfileMenuOpen(false); navigate("/dashboard"); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                Dashboard
              </button>
              <button className="dd-item" onClick={() => { setProfileMenuOpen(false); navigate("/settings"); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                Settings
              </button>
              <button className="dd-item" onClick={() => setProfileMenuOpen(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                Help & Support
              </button>

              <div className="dd-divider" />

              <button className="dd-item dd-item-danger" onClick={handleLogout}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                Log out
              </button>
            </div>
          </div>
        ) : (
          // Login/Signup Button (Before Login)
          <button
            className={`user-button${
              isMobile() ? (signupActive ? " hovered" : "") : hovered ? " hovered" : ""
            }`}
            onMouseEnter={() => !isMobile() && setHovered(true)}
            onMouseLeave={() => !isMobile() && setHovered(false)}
            onClick={handleLoginClick}
          >
            <span
              className={`user-video${
                isMobile() ? (signupActive ? " hide" : "") : hovered ? " hide" : ""
              }`}
            >
              <video
                width="38"
                height="38"
                src="assets/user.mp4"
                autoPlay
                loop
                muted
                playsInline
                style={{
                  borderRadius: "50%",
                  background: "transparent",
                  objectFit: "cover",
                  display: "block",
                }}
              ></video>
            </span>
            <span
              className={`user-signup-text${
                isMobile() ? (signupActive ? " show" : "") : hovered ? " show" : ""
              }`}
            >
              Signup
            </span>
          </button>
        )}

        <div className={`beautiful-toggle ${theme}`} onClick={toggleTheme}>
          <div className="toggle-track">
            <div className="toggle-thumb">
              <div className="scene">
                <svg className="sun-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
                </svg>
                <svg className="moon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`mobile-menu-overlay${mobileMenuOpen ? " open" : ""}`}>
        <div className="mobile-menu-head">
          <div>
            <div className="mobile-menu-brand">SkillOn</div>
            <div className="mobile-menu-sub">Career workspace</div>
          </div>
          <button
            type="button"
            className="close-mobile-menu"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            &times;
          </button>
        </div>
        <nav>
          <ul>
            {menuItems.map((item, index) => (
              <li key={item.label}>
                <a
                  href={item.path || "#features"}
                  onClick={(e) => {
                    if (item.path) {
                      e.preventDefault();
                      navigate(item.path);
                    }
                    setMobileMenuOpen(false);
                  }}
                >
                  <span className="mobile-menu-index">{String(index + 1).padStart(2, "0")}</span>
                  <span>{item.label}</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
      {mobileMenuOpen && (
        <div
          className="drawer-backdrop"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <PricingModal
        open={showPricing}
        onClose={() => setShowPricing(false)}
        onSubscribed={refreshPlan}
      />
    </header>
    </>
  );
};

export default TopMenu;
